import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ProposalLineState, QuoteSummary, VendorQuoteAppliedRow } from '../vendor-quote-flow.types';
import { formatMdy, plural } from '../vendor-quote-flow.util';

export interface ApplyVendorQuotesDialogData {
  /** e.g. "10800 - Toilet Accessories". */
  scopeLabel: string;
  lines: ProposalLineState[];
  quotes: QuoteSummary[];
  /** The quote picked last time, highlighted when the user comes Back from Compare. */
  selected?: number[];
  /** "Include expired quotes" as the user left it, when coming Back from Compare. */
  allowExpired?: boolean;
  /** Applies still in effect on this scope — shown as Applied / Used on, each with Undo. */
  applications?: VendorQuoteAppliedRow[];
  /** Undo one apply; the editor syncs its lines and answers with the scope's fresh state. */
  undo?: (applicationId: number) => Observable<{ applications: VendorQuoteAppliedRow[]; lines: ProposalLineState[] }>;
}

export interface ApplyVendorQuotesDialogResult {
  /** Always the one quote clicked — kept as a list because the apply API takes quote_ids. */
  selected: number[];
  allowExpired: boolean;
}

/** Apply Vendor Quotes to Scope — one whole quote: clicking it opens Compare (mockup G1, G3). */
@Component({
  selector: 'app-apply-vendor-quotes-dialog',
  templateUrl: './apply-vendor-quotes-dialog.component.html',
  styleUrls: ['./apply-vendor-quotes-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ApplyVendorQuotesDialogComponent {
  scopeLabel: string;
  quotes: QuoteSummary[];
  /** The quote picked before Back from Compare — shown highlighted. */
  readonly lastPicked: number | null;
  private scopeProducts: Set<number>;
  plural = plural;
  /** "Include expired quotes" — off by default; the switch only shows when a quote has expired. */
  allowExpired: boolean;
  readonly hasExpired: boolean;
  applications: VendorQuoteAppliedRow[];
  /** The apply being undone right now — its button spins and the others wait. */
  undoingId: number | null = null;

  constructor(
    private dialogRef: MatDialogRef<ApplyVendorQuotesDialogComponent, ApplyVendorQuotesDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) private data: ApplyVendorQuotesDialogData,
    private cd: ChangeDetectorRef,
  ) {
    this.scopeLabel = data.scopeLabel;
    this.quotes = data.quotes;
    this.applications = data.applications ?? [];
    this.scopeProducts = new Set(data.lines.map(l => l.productId));
    this.hasExpired = this.quotes.some(q => q.isExpired);
    this.allowExpired = !!data.allowExpired && this.hasExpired;
    this.lastPicked = data.selected?.[0] ?? null;
  }

  /** The whole-scope apply that used this quote, if it is still in effect. */
  appliedTo(quoteId: number): VendorQuoteAppliedRow | undefined {
    return this.applications.find(a => a.source === 'scope' && a.quoteIds.includes(quoteId));
  }

  /** Single-line picks (Select Vendor Quote) priced from this quote and still in effect. */
  usedOn(quoteId: number): VendorQuoteAppliedRow[] {
    return this.applications.filter(a => a.source === 'material' && a.quoteIds.includes(quoteId));
  }

  /** Applies whose quotes aren't in the list any more (deleted, or off the bid) — still undoable. */
  get otherApplications(): VendorQuoteAppliedRow[] {
    const listed = new Set(this.quotes.map(q => q.quoteId));
    return this.applications.filter(a => !a.quoteIds.some(id => listed.has(id)));
  }

  /** The other quotes a whole-scope apply took along — Undo puts all of them back together. */
  appliedWith(row: VendorQuoteAppliedRow, quoteId: number): string {
    return row.quoteIds.filter(id => id !== quoteId)
      .map(id => this.quotes.find(q => q.quoteId === id)?.vendorName ?? `#${id}`)
      .join(', ');
  }

  appliedWhen(row: VendorQuoteAppliedRow): string {
    const when = row.appliedAt ? formatMdy(row.appliedAt) : '';
    return [when && `on ${when}`, row.appliedBy && `by ${row.appliedBy}`].filter(Boolean).join(' ');
  }

  undoTooltip(row: VendorQuoteAppliedRow, quoteId?: number): string {
    if (!row.canUndo) return row.undoBlockedReason || 'This apply can no longer be undone.';
    const with_ = quoteId ? this.appliedWith(row, quoteId) : '';
    return with_
      ? `Puts the materials back as they were before this apply. ${with_} was applied in the same step and is undone too.`
      : 'Puts the materials back as they were before this apply.';
  }

  /** Undo one apply from here; the dialog stays open with the scope's fresh state. */
  undoApplication(row: VendorQuoteAppliedRow, event: Event): void {
    // The button sits inside the option's <label>: without this the click would also tick the checkbox.
    event.preventDefault();
    event.stopPropagation();
    if (!this.data.undo || !row.canUndo || this.undoingId) return;
    this.undoingId = row.id;
    this.cd.markForCheck();
    this.data.undo(row.id).subscribe({
      next: fresh => {
        this.applications = fresh.applications;
        this.scopeProducts = new Set(fresh.lines.map(l => l.productId));
        this.undoingId = null;
        this.cd.markForCheck();
      },
      // The editor shows the server's reason (e.g. a line was edited since); the row stays.
      error: () => {
        this.undoingId = null;
        this.cd.markForCheck();
      },
    });
  }

  setAllowExpired(on: boolean): void {
    this.allowExpired = on;
    this.cd.markForCheck();
  }

  get materialCount(): number {
    return this.scopeProducts.size;
  }

  /** One click = this quote, straight to Compare. Applied, expired (switch off) and unpriced quotes do nothing. */
  pick(q: QuoteSummary): void {
    if (!this.canPick(q) || this.undoingId !== null) return;
    this.dialogRef.close({ selected: [q.quoteId], allowExpired: this.allowExpired });
  }

  /** Lines that can price a material — the plan ignores unpriced ones, so the counts do too. */
  private priced(q: QuoteSummary) {
    return q.items.filter(i => i.hasPrice);
  }

  unpricedCount(q: QuoteSummary): number {
    return q.items.length - this.priced(q).length;
  }

  canPick(q: QuoteSummary): boolean {
    return !this.appliedTo(q.quoteId) && (!q.isExpired || this.allowExpired) && this.priced(q).length > 0;
  }

  blockedReason(q: QuoteSummary): string {
    // Applied: the row's own "Applied" badge and Undo say it, so the row adds no tooltip of its own.
    if (this.appliedTo(q.quoteId)) return '';
    if (q.isExpired && !this.allowExpired) return 'This quote has expired. Turn on "Include expired quotes" to use it anyway.';
    if (!this.priced(q).length) return 'No line on this quote has a price. Edit the quote to add them.';
    if (q.isExpired) return 'This quote has expired. Confirm the prices with the vendor.';
    return '';
  }

  /** How many of the scope's materials this quote covers (G1). */
  covers(q: QuoteSummary): number {
    return new Set(this.priced(q).filter(i => this.scopeProducts.has(i.productId)).map(i => i.productId)).size;
  }

  /** Quoted products the scope doesn't have (G1 "adds N new"). */
  adds(q: QuoteSummary): number {
    return new Set(this.priced(q).filter(i => !this.scopeProducts.has(i.productId)).map(i => i.productId)).size;
  }

  formatDate(value: string): string {
    return formatMdy(value);
  }

  trackByQuote(index: number, q: QuoteSummary): number {
    return q.quoteId;
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
